import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import styles from './Subjects.module.css';
import { subjectApi } from 'features/subject-management/api/subjectApi';
import { moduleApi } from 'features/subject-management/api/moduleApi';
import { Subject, Module } from 'features/subject-management/types/subject';
import SubjectCard from 'features/subject-management/components/SubjectCard/SubjectCard';
import ModuleCard from 'features/subject-management/components/ModuleCard/ModuleCard';
import ModuleDetail from 'features/subject-management/components/ModuleDetail/ModuleDetail';
import Loading from 'components/Loading/Loading';
import { subjects as subjectMetadata } from './constants';

export default function Subjects() {
    const { subjectSlug, moduleSlug } = useParams<{ subjectSlug?: string; moduleSlug?: string }>();
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [modules, setModules] = useState<Module[]>([]);
    const [currentSubject, setCurrentSubject] = useState<Subject | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                if (moduleSlug) {
                    // ModuleDetail handles its own fetching, but we might want currentSubject for context
                    if (subjectSlug && !currentSubject) {
                        const sRes = await subjectApi.getBySlug(subjectSlug);
                        setCurrentSubject(sRes.data);
                    }
                } else if (subjectSlug) {
                    const sRes = await subjectApi.getBySlug(subjectSlug);
                    setCurrentSubject(sRes.data);
                    const mRes = await moduleApi.getAll(sRes.data.id);
                    setModules(mRes.data);
                } else {
                    const res = await subjectApi.getAll();
                    setSubjects(res.data);
                }
            } catch (error) {
                console.error('Error fetching catalog data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [subjectSlug, moduleSlug]);

    if (moduleSlug) {
        return (
            <section className={styles.subjectsSection}>
                <div className={styles.breadcrumbHeader}>
                    <Link to="/library" className={styles.breadcrumbLink} replace={true}>
                        <span className="material-symbols-outlined">
                            home
                        </span>
                    </Link>
                    <span className={styles.breadcrumbSeparator}>/</span>
                    <Link to={`/library/${subjectSlug}`} className={styles.breadcrumbLink} replace={true}>
                        {currentSubject?.name || 'Subject'}
                    </Link>
                    <span className={styles.breadcrumbSeparator}>/</span>
                    <span className={styles.breadcrumbCurrent}>{modules.find(m => m.slug === moduleSlug)?.name || 'Module'}</span>
                </div>
                <ModuleDetail isCatalogView={true} />
            </section>
        );
    }

    return (
        <div className={styles.container}>
            {subjectSlug && !moduleSlug && currentSubject && (
                <div className={styles.heroSection}
                    style={{
                        // backgroundImage: `url(${currentSubject.thumbnail_url || currentSubject.api_img_url || 'https://picsum.photos/seed/science-module-1/400/300'})`,
                    }}>
                    <div className={styles.heroWrapper}
                        style={{
                            backgroundImage: `url('https://picsum.photos/seed/science-module-1/400/300')`,
                        }}
                    />
                    <div className={styles.heroOverlay} />
                    <span className={`material-symbols-outlined ${styles.heroIcon}`}>
                        {subjectMetadata.find(s => s.name.toLowerCase() === currentSubject.name.toLowerCase())?.icon || 'category'}
                    </span>
                    <h1 className={styles.heroTitle}>
                        {currentSubject.name}
                    </h1>
                </div>
            )}
            <section className={styles.subjectsSection}>
                <div className={styles.header}>
                    {subjectSlug && (
                        <div className={styles.breadcrumbHeaderCentered}>
                            <Link to="/library" className={styles.breadcrumbLink} replace={true}>
                                <span className="material-symbols-outlined">
                                    home
                                </span>
                            </Link>
                            <span className={styles.breadcrumbSeparator}>/</span>
                            <span className={styles.breadcrumbCurrent}>{currentSubject?.name}</span>
                        </div>
                    )}
                    <h2 className={styles.title}>
                        {subjectSlug ? '' : 'Explore by Subject'}
                    </h2>
                </div>

                {loading ? (
                    <Loading message="Loading content..." />
                ) : (
                    <div className={styles.gridContainer}>
                        {subjectSlug ? (
                            <div className={styles.customGrid}>
                                {modules.map((module) => (
                                    <ModuleCard
                                        key={module.id}
                                        module={module}
                                        subjectSlug={subjectSlug}
                                        isStudent={false}
                                        isSuperAdmin={false}
                                        showOnlyImageAndName={true}
                                        linkTo={`/library/${subjectSlug}/${module.slug}`}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className={styles.customGrid}>
                                {subjects.map((subject) => (
                                    <SubjectCard
                                        key={subject.id}
                                        subject={subject}
                                        isStudent={false}
                                        isSuperAdmin={false}
                                        showOnlyImageAndName={true}
                                        linkTo={`/library/${subject.slug}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}
